import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HiOutlineMicrophone, HiMicrophone, HiOutlineTrash, HiOutlineArrowRight } from 'react-icons/hi2';
import {
  MicButton,
  SendButton,
  VoiceCancelButton,
  VoiceLevelFill,
  VoiceRecordBar,
  VoiceRecordTime,
} from '../../styles/UserWorkspacePageStyles';
import { formatVoiceTime } from './VoicePlayer';
import { createMicLevelMonitor } from '../../utils/webrtcCall';
import { useT } from '../../LocaleContext';
import { t as translate } from '../../i18n';

const MAX_VOICE_MS = 60_000;
const MIN_VOICE_MS = 400;
const VOICE_BITRATE = 128_000;

const pickRecorderMime = () => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  if (typeof window === 'undefined' || !window.MediaRecorder?.isTypeSupported) {
    return '';
  }
  return types.find((type) => window.MediaRecorder.isTypeSupported(type)) || '';
};

const VoiceComposer = ({ chatId, onSend, onError, children }) => {
  const t = useT();
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const actionRef = useRef('discard');
  const chatIdRef = useRef(chatId);
  const levelMonitorRef = useRef(null);

  useEffect(() => {
    chatIdRef.current = chatId;
  }, [chatId]);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopLevelMonitor = () => {
    if (levelMonitorRef.current) {
      levelMonitorRef.current.stop();
      levelMonitorRef.current = null;
    }
    setMicLevel(0);
  };

  const stopStream = () => {
    stopLevelMonitor();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const finishRecording = useCallback((blob, durationMs) => {
    setRecording(false);
    setElapsedMs(0);
    if (!blob || blob.size < 64 || durationMs < MIN_VOICE_MS) {
      return;
    }
    onSend(blob, durationMs);
  }, [onSend]);

  const stopRecorder = useCallback((action) => {
    actionRef.current = action;
    clearTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }
    stopStream();
    setRecording(false);
    setElapsedMs(0);
  }, []);

  const cancelRecording = useCallback(() => {
    stopRecorder('discard');
  }, [stopRecorder]);

  useEffect(() => {
    return () => {
      cancelRecording();
    };
  }, [chatId, cancelRecording]);

  const startingRef = useRef(false);

  const startRecording = async () => {
    if (recording || busy || !chatId || startingRef.current) {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      onError?.(translate('voice.notSupported'));
      return;
    }
    startingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: { ideal: 1 },
          sampleRate: { ideal: 48000 },
          sampleSize: { ideal: 16 },
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = pickRecorderMime();
      const recorderOptions = {
        audioBitsPerSecond: VOICE_BITRATE,
        bitsPerSecond: VOICE_BITRATE,
      };
      if (mime) {
        recorderOptions.mimeType = mime;
      }
      const recorder = new MediaRecorder(stream, recorderOptions);
      recorderRef.current = recorder;
      actionRef.current = 'discard';
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        clearTimer();
        stopStream();
        recorderRef.current = null;
        const durationMs = Date.now() - startedAtRef.current;
        const type = recorder.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        setRecording(false);
        if (actionRef.current === 'send') {
          finishRecording(blob, durationMs);
        }
      };
      recorder.start(200);
      startedAtRef.current = Date.now();
      setElapsedMs(0);
      setRecording(true);
      const monitor = createMicLevelMonitor(stream, (level) => {
        setMicLevel(level);
      });
      levelMonitorRef.current = monitor;
      monitor.start();
      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current;
        setElapsedMs(elapsed);
        if (elapsed >= MAX_VOICE_MS) {
          stopRecorder('send');
        }
      }, 100);
    } catch (error) {
      stopStream();
      if (error?.name === 'NotAllowedError') {
        onError?.(translate('voice.micDenied'));
      } else {
        onError?.(translate('voice.startFailed'));
      }
    } finally {
      startingRef.current = false;
    }
  };

  const sendRecording = async () => {
    if (!recording || busy) {
      return;
    }
    setBusy(true);
    try {
      stopRecorder('send');
    } finally {
      setBusy(false);
    }
  };

  if (recording) {
    return (
      <>
        <VoiceCancelButton
          type="button"
          onClick={cancelRecording}
          aria-label={t('voice.cancel')}
        >
          <HiOutlineTrash />
        </VoiceCancelButton>
        <MicButton type="button" $active $level={micLevel} tabIndex={-1} aria-label={t('voice.record')}>
          <VoiceLevelFill $level={micLevel} />
          <HiMicrophone />
        </MicButton>
        <VoiceRecordBar>
          <VoiceRecordTime>{formatVoiceTime(elapsedMs)}</VoiceRecordTime>
        </VoiceRecordBar>
        <SendButton type="button" onClick={sendRecording} aria-label={t('voice.send')}>
          <HiOutlineArrowRight />
        </SendButton>
      </>
    );
  }

  return (
    <>
      <MicButton
        type="button"
        disabled={!chatId || busy}
        onClick={startRecording}
        aria-label={t('voice.record')}
      >
        <HiOutlineMicrophone />
      </MicButton>
      {children}
    </>
  );
};

export default VoiceComposer;
